import { useCollaboratorStore } from '@/stores/collaborators'
import { Delete, Settings } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { EnumAccessLevel } from '../../../@types'
import { bodyRowCell, headerRowCell } from '../../../components/table/@types'
import { useAuth } from '../../../hooks'
import services from '../../../services'

export function useData() {
  const ROLES = useMemo<Array<EnumAccessLevel>>(
    () => ['ADMIN', 'FINANCE', 'STUDENT', 'TEACHER'],
    [],
  )
  const { user } = useAuth()
  const { setCollaboratorID } = useCollaboratorStore()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['/collaborators'],
    queryFn: services.user.getCollaborators,
  })

  const [users, setUsers] = useState(data ?? [])
  const [loading, setLoading] = useState<boolean>(false)

  const onFilterChange = (query: string) => {
    if (!query) setUsers(data ?? [])
    else {
      const filtered = users.filter(
        ({ name, surname }) =>
          name?.toLowerCase().includes(query) ||
          surname?.toLowerCase().includes(query),
      )
      setUsers(filtered)
    }
  }

  const headRows: headerRowCell[] = useMemo(
    () => [
      { hKey: 'id', label: '#' },
      { hKey: 'name', label: 'Name' },
      { hKey: 'surname', label: 'surname' },
      { hKey: 'email', label: 'E-mail' },
      { hKey: 'accessLevel', label: 'Access Level' },
      { hKey: 'createdAt', label: 'Created At' },
      { hKey: '#', label: 'Actions' },
    ],
    [],
  )

  const deleteItem = async (id: string) => {
    setLoading(true)
    await services.user.deleteById(id).finally(() => {
      refetch().finally(() => setLoading(false))
    })
  }

  const CellActions = (userId: string) => (
    <div className="flex flex-wrap gap-1 items-center">
      <IconButton
        sx={{ color: 'white' }}
        onClick={() => {
          setCollaboratorID(userId)
        }}
      >
        <Settings />
      </IconButton>

      <IconButton
        disabled={user?.id === userId}
        sx={{ color: 'white' }}
        onClick={() => deleteItem(userId)}
      >
        <Delete />
      </IconButton>
    </div>
  )

  const bodyRows: bodyRowCell[] = useMemo(() => {
    const formattedRows: Array<bodyRowCell> = []

    if (users) {
      const collaborators = users.filter(
        (user) => user.accessLevel !== 'STUDENT',
      )

      let count = 1
      for (const collaborator of collaborators) {
        let formattedCell: bodyRowCell | null = null
        for (const { hKey } of headRows) {
          let label: React.ReactNode = null
          if (hKey === '#') label = CellActions(collaborator.id)
          else if (hKey === 'id') {
            if (user?.id === collaborator[hKey]) label = `${count} (Myself)`
            else label = String(count)
          } else if (hKey === 'createdAt')
            label = format(collaborator[hKey], 'MMM dd yyyy')
          else label = collaborator[hKey as keyof typeof collaborator] as string

          formattedCell = { label, bKey: hKey }
          formattedRows.push(formattedCell)
        }

        count += 1
      }
    }

    return formattedRows

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headRows, users])

  useEffect(() => {
    if (data) {
      setUsers(data)
    }
  }, [data])

  return {
    isLoading: isLoading || loading,
    error,
    users,
    bodyRows,
    headRows,
    ROLES,
    CellActions,
    refetch,
    onFilterChange,
    deleteItem,
  }
}
